import React, { useState, useEffect } from 'react';
import { firestore } from '../firebase'; // Import firestore from firebase.js
import { collection, getDocs, query, where, updateDoc, doc } from 'firebase/firestore';
import { FaStar, FaRegStar, FaArrowLeft, FaArrowRight, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa'; // Import star and arrow icons
import * as XLSX from 'xlsx'; // Import XLSX library

const KoalasWaitlist = () => {
  const [waitlistData, setWaitlistData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [birthdayFromDate, setBirthdayFromDate] = useState('');
  const [birthdayToDate, setBirthdayToDate] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

  useEffect(() => {
    const fetchWaitlistData = async () => {
      try {
        const studentsQuery = query(collection(firestore, 'students'), where('enquiry.whichWaitlist', '==', 'Main'));
        const querySnapshot = await getDocs(studentsQuery);
        const fetchedData = [];

        querySnapshot.forEach((doc) => {
          const data = doc.data();

          if (data.enquiry && data.children && data.children[0] && data.parent && data.enquiry.whichWaitlist === 'Main') {
            const enquiryDate = data.enquiry.enquiryDate.toDate();

            let childBirthday;
            if (data.children[0].dateOfBirth.toDate) {
              childBirthday = data.children[0].dateOfBirth.toDate();
            } else if (typeof data.children[0].dateOfBirth === 'string') {
              childBirthday = new Date(data.children[0].dateOfBirth);
            } else {
              childBirthday = data.children[0].dateOfBirth;
            }

            // Calculate age based on date of birth
            const ageInMonths = calculateAgeInMonths(childBirthday);

            // Determine class based on age
            const childClass = getClassBasedOnAge(ageInMonths);

            if (childClass === 'Koalas') { // Only add Cubs class data
              fetchedData.push({
                id: doc.id,
              childFirstName: data.children[0].firstName,
              childLastName: data.children[0].lastName,
              class: childClass,
              childBirthday: childBirthday.toLocaleDateString(),
              status: data.enquiry.status,
              priority: data.enquiry.priority,
              enquiryDate: enquiryDate.toLocaleDateString(),
              siblings: data.children[0].siblings || [],
              parentFirstName: data.parent.parentFirstName,
              parentLastName: data.parent.parentLastName,
              });
            }
          } else {
            console.error('Document data structure is incorrect:', data);
          }
        });

        setWaitlistData(fetchedData);
        setFilteredData(fetchedData); // Initially, the filtered data is the same as the complete data
      } catch (error) {
        console.error('Error fetching waitlist data: ', error);
      }
    };

    fetchWaitlistData();
  }, []);

  useEffect(() => {
    const lowercasedQuery = searchQuery.toLowerCase();
    const filtered = waitlistData.filter(item => {
      const childBirthdayDate = new Date(item.childBirthday);
      const fromDate = birthdayFromDate ? new Date(`${birthdayFromDate}T00:00:00`) : null;
      const toDate = birthdayToDate ? new Date(`${birthdayToDate}T23:59:59`) : null;

      if (fromDate && childBirthdayDate < fromDate) {
        return false;
      }

      if (toDate && childBirthdayDate > toDate) {
        return false;
      }

      return (
        item.childFirstName.toLowerCase().includes(lowercasedQuery) ||
        item.childLastName.toLowerCase().includes(lowercasedQuery) ||
        item.class.toLowerCase().includes(lowercasedQuery) ||
        item.childBirthday.toLowerCase().includes(lowercasedQuery) ||
        item.status.toLowerCase().includes(lowercasedQuery) ||
        item.enquiryDate.toLowerCase().includes(lowercasedQuery) ||
        (item.siblings && item.siblings.length.toString().includes(lowercasedQuery)) ||
        item.parentFirstName.toLowerCase().includes(lowercasedQuery) ||
        item.parentLastName.toLowerCase().includes(lowercasedQuery)
      );
    });
    setFilteredData(filtered);
    setCurrentPage(1);
  }, [searchQuery, birthdayFromDate, birthdayToDate, waitlistData]);


  const handleViewPerson = (person) => {
    window.open(`/personDetail/${person.id}`); // Navigate to PersonDetail with person ID
  };

  const calculateAgeInMonths = (dateOfBirth) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    const yearsDifference = today.getFullYear() - birthDate.getFullYear();
    const monthsDifference = today.getMonth() - birthDate.getMonth();
    const daysDifference = today.getDate() - birthDate.getDate();

    let ageInMonths = yearsDifference * 12 + monthsDifference;
    if (daysDifference < 0) {
      ageInMonths--;
    }

    return ageInMonths;
  };

  const getClassBasedOnAge = (ageInMonths) => {
    switch (true) {
      case (ageInMonths >= 2 && ageInMonths < 12): // 8 weeks to <12 months
        return "Cubs";
      case (ageInMonths >= 12 && ageInMonths < 18): // 12 to <18 months
        return "Teddies";
      case (ageInMonths >= 18 && ageInMonths < 24): // 18 to <24 months
        return "Koalas";
      case (ageInMonths >= 24 && ageInMonths < 36): // 2 to <3 years
        return "Pandas";
      case (ageInMonths >= 36 && ageInMonths < 48): // 3 to <4 years
        return "Preschool";
      case (ageInMonths >= 48 && ageInMonths < 60): // 4 to <5 years
        return "Pre-K";
      case (ageInMonths >= 60): // 5 years and older
        return "Kindergarten";
      default:
        return "Unknown"; // Added a default case for safety
    }
  };

  const handleEntriesPerPageChange = (event) => {
    setEntriesPerPage(Number(event.target.value));
    setCurrentPage(1); // Reset to first page
  };

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => (prev * entriesPerPage < filteredData.length ? prev + 1 : prev));
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = React.useMemo(() => {
    let sortableData = [...filteredData];
    if (sortConfig.key !== null) {
      sortableData.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === 'childBirthday' || sortConfig.key === 'enquiryDate') {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableData;
  }, [filteredData, sortConfig]);

  // Get current entries to display
  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = sortedData.slice(indexOfFirstEntry, indexOfLastEntry);

  const handleExport = () => {
    const worksheet = XLSX.utils.json_to_sheet(waitlistData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Koalas Waitlist");
    XLSX.writeFile(workbook, "Koalas_Waitlist.xlsx");
  };

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      if (sortConfig.direction === 'ascending') {
        return <FaSortUp />;
      } else {
        return <FaSortDown />;
      }
    } else {
      return <FaSort />;
    }
  };

  const handlePriorityToggle = async (person) => {
    try {
      const newPriorityStatus = !person.priority;
      const docRef = doc(firestore, 'students', person.id);
      await updateDoc(docRef, {
        'enquiry.priority': newPriorityStatus,
      });
      setWaitlistData(prevData => 
        prevData.map(item => 
          item.id === person.id ? { ...item, priority: newPriorityStatus } : item
        )
      );
      setFilteredData(prevData => 
        prevData.map(item => 
          item.id === person.id ? { ...item, priority: newPriorityStatus } : item
        )
      );
    } catch (error) {
      console.error('Error updating priority status:', error);
    }
  };

  return (
    <div className="KoalasWaitlist p-4">
      <h1 className="text-3xl font-serif mb-4">Koalas Waitlist</h1>
      <div className="mb-4 rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-12 md:items-end">
          <div className="md:col-span-2">
            <label className="mb-1 block text-sm text-gray-700">Entries per page</label>
            <select
              value={entriesPerPage}
              onChange={handleEntriesPerPageChange}
              className="h-10 w-full rounded border border-gray-300 px-2"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
          <div className="md:col-span-4">
            <label className="mb-1 block text-sm text-gray-700">Search</label>
            <input
              type="text"
              placeholder="Name, class, status, parent..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-10 w-full rounded border border-gray-300 px-3"
            />
          </div>
          <div className="md:col-span-2">
            <label className="mb-1 block text-sm text-gray-700">DOB from</label>
            <input
              type="date"
              value={birthdayFromDate}
              onChange={(e) => setBirthdayFromDate(e.target.value)}
              className="h-10 w-full rounded border border-gray-300 px-2"
            />
          </div>
          <div className="md:col-span-2">
            <label className="mb-1 block text-sm text-gray-700">DOB to</label>
            <input
              type="date"
              value={birthdayToDate}
              onChange={(e) => setBirthdayToDate(e.target.value)}
              className="h-10 w-full rounded border border-gray-300 px-2"
            />
          </div>
          <div className="md:col-span-2">
            <button
              className="h-10 w-full rounded bg-green-500 px-4 text-white hover:bg-green-600"
              onClick={handleExport}
            >
              Export
            </button>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border-gray-200 shadow-md rounded-lg">
          <thead className="bg-gray-100 text-gray-800">
            <tr>
            <th className="py-2 px-4 border-b border-gray-200 text-center">#</th>
              <th className="py-2 px-4 border-b border-gray-200 text-center cursor-pointer" onClick={() => handleSort('childFirstName')}>
                <div className="flex items-center justify-center">
                  Child First Name {getSortIcon('childFirstName')}
                </div>
              </th>
              <th className="py-2 px-4 border-b border-gray-200 text-center cursor-pointer" onClick={() => handleSort('childLastName')}>
                <div className="flex items-center justify-center">
                  Child Last Name {getSortIcon('childLastName')}
                </div>
              </th>
              <th className="py-2 px-4 border-b border-gray-200 text-center cursor-pointer" onClick={() => handleSort('class')}>
                <div className="flex items-center justify-center">
                  Class {getSortIcon('class')}
                </div>
              </th>
              <th className="py-2 px-4 border-b border-gray-200 text-center cursor-pointer" onClick={() => handleSort('childBirthday')}>
                <div className="flex items-center justify-center">
                  Child Birthday {getSortIcon('childBirthday')}
                </div>
              </th>
              <th className="py-2 px-4 border-b border-gray-200 text-center cursor-pointer" onClick={() => handleSort('status')}>
                <div className="flex items-center justify-center">
                  Status {getSortIcon('status')}
                </div>
              </th>
              <th className="py-2 px-4 border-b border-gray-200 text-center cursor-pointer" onClick={() => handleSort('priority')}>
                <div className="flex items-center justify-center">
                  Priority {getSortIcon('priority')}
                </div>
              </th>
              <th className="py-2 px-4 border-b border-gray-200 text-center cursor-pointer" onClick={() => handleSort('enquiryDate')}>
                <div className="flex items-center justify-center">
                  Enquiry Date {getSortIcon('enquiryDate')}
                </div>
              </th>
              <th className="py-2 px-4 border-b border-gray-200 text-center cursor-pointer" onClick={() => handleSort('siblings')}>
                <div className="flex items-center justify-center">
                  Siblings {getSortIcon('siblings')}
                </div>
              </th>
              <th className="py-2 px-4 border-b border-gray-200 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="text-gray-700">
            {currentEntries.map((item, index) => (
              <tr key={item.id} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                <td className="py-2 px-4 border-b border-gray-200 text-center">{index + 1 + indexOfFirstEntry}</td>
                <td className="py-2 px-4 border-b border-gray-200 text-center">{item.childFirstName}</td>
                <td className="py-2 px-4 border-b border-gray-200 text-center">{item.childLastName}</td>
                <td className="py-2 px-4 border-b border-gray-200 text-center">{item.class}</td>
                <td className="py-2 px-4 border-b border-gray-200 text-center">{item.childBirthday}</td>
                <td className="py-2 px-4 border-b border-gray-200 text-center">{item.status}</td>
                <td className="py-2 px-4 border-b border-gray-200 text-center">
                  <div className="flex justify-center items-center h-full cursor-pointer" onClick={() => handlePriorityToggle(item)}>
                    {item.priority ? <FaStar className="text-yellow-500" size={25} /> : <FaRegStar className="text-gray-500" size={25} />}
                  </div>
                </td>
                <td className="py-2 px-4 border-b border-gray-200 text-center">{item.enquiryDate}</td>
                <td className="py-2 px-4 border-b border-gray-200 text-center">{item.siblings ? item.siblings.length : 0}</td>
                <td className="py-2 px-4 border-b border-gray-200 text-center">
                  <button className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600" onClick={() => handleViewPerson(item)}>View</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-start mt-4 space-x-2">
        <button
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          onClick={handlePreviousPage}
          disabled={currentPage === 1}
        >
          <FaArrowLeft />
        </button>
        <button
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          onClick={handleNextPage}
          disabled={indexOfLastEntry >= filteredData.length}
        >
          <FaArrowRight />
        </button>
      </div>
    </div>
  );
};

export default KoalasWaitlist;
